import { useState } from 'react'
import type { Person, Unit } from '@/api/pending'
import { Button, Chip, Eyebrow, Modal, Segmented, SummaryStrip } from '@/ui'
import { S } from './strings'
import styles from './AttachModal.module.css'

export type AttachScope = 'resident' | 'reviewer' | 'admin'

interface AttachModalProps {
  person: Person
  units: Unit[]
  pending: boolean
  error: boolean
  onClose: () => void
  onBack: () => void
  onAttach: (unit: Unit, scope: AttachScope) => void
}

/** Step 2: pick the unit and the scope granted. */
export function AttachModal({
  person,
  units,
  pending,
  error,
  onClose,
  onBack,
  onAttach,
}: AttachModalProps) {
  const [unitId, setUnitId] = useState<number | null>(null)
  const [scope, setScope] = useState<AttachScope>('resident')
  const unit = units.find((u) => u.id === unitId)
  const name = `${person.first_name} ${person.last_name}`

  return (
    <Modal
      title={S.step2Title(person.first_name)}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" icon="chevron_left" onClick={onBack}>
            {S.back}
          </Button>
          <Button disabled={!unit} loading={pending} onClick={() => unit && onAttach(unit, scope)}>
            {S.attach}
          </Button>
        </>
      }
    >
      <SummaryStrip
        name={name}
        subtitle={`${person.email} · CPF ${person.tax_id_masked}`}
        aside={
          <button type="button" className={styles.link} onClick={onBack}>
            {S.notThem}
          </button>
        }
      />
      <div>
        <Eyebrow>{S.unit}</Eyebrow>
        <div className={styles.chips}>
          {units.map((u) => (
            <Chip key={u.id} selected={u.id === unitId} onClick={() => setUnitId(u.id)}>
              {u.number} · {u.tower}
              {u.residents.length === 0 && (
                <>
                  {' · '}
                  <span className={styles.empty}>{S.empty}</span>
                </>
              )}
            </Chip>
          ))}
        </div>
      </div>
      <div>
        <Eyebrow>{S.scope}</Eyebrow>
        <div className={styles.scope}>
          <Segmented<AttachScope>
            aria-label={S.scope}
            value={scope}
            onChange={setScope}
            items={[
              { id: 'resident', label: 'Morador' },
              { id: 'reviewer', label: 'Revisador' },
              { id: 'admin', label: 'Administrador' },
            ]}
          />
        </div>
      </div>
      {error && (
        <p role="alert" className={styles.error}>
          {S.attachError}
        </p>
      )}
    </Modal>
  )
}
